import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  InputAdornment,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import api from '../api/client';
import { getModernwashLegacySubscriptions } from '../api/modernwashLegacy';
import { actionBtnSx, statusChipSx, alertBadgeSx } from '../styles/uiTokens';
import GrantMembershipDialog, { canGrantMembership } from '../components/GrantMembershipDialog';

// 공통 UI 토큰 사용 (frontend/src/styles/uiTokens.js)
const statusMap = {
  active:                 { label: '활성',    variant: 'success' },
  cancellation_requested: { label: '해지예약', variant: 'warning' },
  cancelled:              { label: '취소',    variant: 'danger' },
  refunded:               { label: '환불',    variant: 'purple' },
};

const productMap = {
  daily_subscription: '데일리',
  all_in_one_subscription: '올인원',
};

// 만료까지 남은 일수 계산
function daysUntilExpiry(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * [환불 가능 여부] — 서버가 계산한 계약 필드만 사용한다.
 *
 * paymentKey 같은 결제 공급자 식별자는 브라우저에 내려오지 않는다. 서버가 내부 결제 정보와
 * 수동 지급 여부를 확인해 `refundEligible`로 반환하고, 이 화면은 그 값으로만 버튼을 연다.
 * 실제 환불 권한·상태 검사는 서버가 다시 수행한다.
 */
function canRefund(sub) {
  return sub?.refundEligible === true;
}

const SubscriptionsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [subscriptions, setSubscriptions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortOrder, setSortOrder] = useState('asc');
  const [productFilter, setProductFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState(''); // '7' | '30' | ''

  // 위험 액션 확인 모달 — 운영자가 정보 확인 후에만 진행 (window.confirm 대체)
  // action: 'unsubscribe' | 'revert' | 'refund'
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, sub: null });
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // 이용권 수동 지급 (결제 없이 기간 지정 부여) — master/manager만
  const [grantOpen, setGrantOpen] = useState(false);
  const canGrant = canGrantMembership();
  const [dataSource, setDataSource] = useState('mogoo');
  const isLegacySource = dataSource === 'modernwash';

  const openActionDialog = (action, sub) => {
    setActionDialog({ open: true, action, sub });
    setActionReason('');
  };
  const closeActionDialog = () => {
    if (actionLoading) return;
    setActionDialog({ open: false, action: null, sub: null });
    setActionReason('');
  };

  useEffect(() => {
    fetchSubscriptions();
    // fetchSubscriptions는 현재 원천 선택값 외 다른 상태를 캡처하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      if (isLegacySource) {
        const legacy = await getModernwashLegacySubscriptions();
        setSubscriptions((legacy.data?.subscriptions || []).map((subscription) => ({
          _id: subscription.id,
          userId: {
            _id: subscription.user.id,
            memberNickname: subscription.user.nickname,
            memberEmail: subscription.user.email,
            memberPhone: subscription.user.phone,
          },
          vehicleLicensePlate: subscription.licensePlate,
          productId: subscription.productId,
          washCount: subscription.washCount,
          expirationDate: subscription.expirationDate,
          status: subscription.status,
          refundEligible: false,
          scheduledChanges: [],
        })));
      } else {
        const res = await api.get('/subscriptions/admin/subscriptions/active');
        setSubscriptions(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error('구독 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  // 해지/취소 액션 진입점 — 상태에 따라 자동 분기
  const handleUnsubscribe = (sub) => {
    const isCancellationState = sub.status === 'cancellation_requested' ||
      (sub.scheduledChanges || []).some(c => c.kind === 'cancellation' && c.status === 'scheduled');
    openActionDialog(isCancellationState ? 'revert' : 'unsubscribe', sub);
  };

  // 환불 액션 진입점
  const handleRefund = (sub) => openActionDialog('refund', sub);

  // 모달 확인 시 실제 API 호출
  // reason은 백엔드가 SubscriptionHistory.meta에 기록 (감사 로그용)
  const handleConfirmAction = async () => {
    const { action, sub } = actionDialog;
    if (!sub) return;

    // 환불은 사유 필수 (감사·분쟁 대응)
    if (action === 'refund' && !actionReason.trim()) {
      alert('환불 사유를 입력해주세요.');
      return;
    }

    setActionLoading(true);
    try {
      const reason = actionReason.trim() || undefined;
      if (action === 'revert') {
        await api.post(`/subscriptions/admin/subscriptions/${sub._id}/cancellation/revert`, { reason });
        alert('해지 예약이 취소되었습니다.');
      } else if (action === 'unsubscribe') {
        await api.post('/subscriptions/admin/subscriptions/unsubscribe', {
          userId: sub.userId?._id,
          subscriptionId: sub._id,
          reason,
        });
        alert('구독 해지가 예약되었습니다.');
      } else if (action === 'refund') {
        await api.post('/subscriptions/admin/subscriptions/refund', {
          userId: sub.userId?._id,
          subscriptionId: sub._id,
          reason,
        });
        alert('환불이 완료되었습니다.');
      }
      closeActionDialog();
      fetchSubscriptions();
    } catch (err) {
      const msg = err?.response?.data?.message;
      alert(msg || (
        action === 'refund' ? '환불에 실패했습니다.' :
        action === 'revert' ? '해지 예약 취소에 실패했습니다.' :
        '구독 해지에 실패했습니다.'
      ));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR');
  };

  // 필터 적용
  const filtered = subscriptions.filter((sub) => {
    if (search) {
      const lower = search.toLowerCase();
      const matchText =
        sub.userId?.memberNickname?.toLowerCase().includes(lower) ||
        sub.userId?.memberEmail?.toLowerCase().includes(lower) ||
        sub.userId?.memberPhone?.includes(search) ||
        sub.vehicleLicensePlate?.toLowerCase().includes(lower);
      if (!matchText) return false;
    }
    if (productFilter && sub.productId !== productFilter) return false;
    if (expiryFilter) {
      const days = daysUntilExpiry(sub.expirationDate);
      if (days === null || days > Number(expiryFilter)) return false;
    }
    return true;
  });

  // 만료일 정렬
  const sorted = [...filtered].sort((a, b) => {
    const dateA = new Date(a.expirationDate || 0).getTime();
    const dateB = new Date(b.expirationDate || 0).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleDataSourceChange = (_, nextSource) => {
    if (!nextSource) return;
    setDataSource(nextSource);
    setPage(1);
  };

  // 클라이언트 사이드 페이지네이션
  const totalPages = Math.ceil(sorted.length / limit);
  const paged = sorted.slice((page - 1) * limit, page * limit);

  // 만료 7일 이내인 활성 구독 수 (배지용)
  const expiringSoonCount = subscriptions.filter((s) => {
    const d = daysUntilExpiry(s.expirationDate);
    return d !== null && d <= 7 && s.status === 'active';
  }).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
            구독 관리
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              활성 구독 {filtered.length}건
            </Typography>
            {isLegacySource && <Typography variant="caption" color="info.main">ModernWash 전주점 기존 데이터 · 읽기 전용</Typography>}
            {expiringSoonCount > 0 && (
              <Box sx={alertBadgeSx('warning')}>
                <WarningAmberIcon sx={{ fontSize: 15 }} />
                7일 내 만료 {expiringSoonCount}건
              </Box>
            )}
          </Box>
        </Box>
        {canGrant && !isLegacySource && (
          <Button
            variant="contained"
            startIcon={<CardMembershipIcon />}
            onClick={() => setGrantOpen(true)}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            이용권 부여
          </Button>
        )}
      </Box>

      <ToggleButtonGroup exclusive value={dataSource} onChange={handleDataSourceChange} size="small" color="primary" sx={{ mb: 2 }}>
        <ToggleButton value="mogoo">모구 구독</ToggleButton>
        <ToggleButton value="modernwash">ModernWash 전주점 기존 구독</ToggleButton>
      </ToggleButtonGroup>

      {/* 필터 영역 */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="사용자명, 이메일, 전화번호, 차량번호로 검색"
          value={search}
          onChange={handleFilterChange(setSearch)}
          slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          } }}
          sx={{ width: { xs: '100%', sm: 320 } }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>상품</InputLabel>
          <Select value={productFilter} label="상품" onChange={handleFilterChange(setProductFilter)}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="daily_subscription">데일리</MenuItem>
            <MenuItem value="all_in_one_subscription">올인원</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>만료 예정</InputLabel>
          <Select value={expiryFilter} label="만료 예정" onChange={handleFilterChange(setExpiryFilter)}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="7">7일 이내</MenuItem>
            <MenuItem value="30">30일 이내</MenuItem>
          </Select>
        </FormControl>
        {/* 모바일은 카드 리스트라 표시 개수 변경 불필요 */}
        <FormControl size="small" sx={{ minWidth: 100, display: { xs: 'none', md: 'inline-flex' } }}>
          <InputLabel>표시</InputLabel>
          <Select
            value={limit}
            label="표시"
            onChange={(e) => { setLimit(e.target.value); setPage(1); }}
          >
            <MenuItem value={10}>10개</MenuItem>
            <MenuItem value={20}>20개</MenuItem>
            <MenuItem value={50}>50개</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* ── 모바일: 카드 리스트 뷰 ── */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#888' }}>로딩 중...</Paper>
          ) : paged.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#888' }}>
              {expiryFilter || productFilter || search ? '필터 조건에 맞는 구독이 없습니다.' : '활성 구독이 없습니다.'}
            </Paper>
          ) : (
            paged.map((sub) => {
              const status = statusMap[sub.status] || { label: sub.status, variant: 'neutral' };
              const isCancellationState = sub.status === 'cancellation_requested' ||
                (sub.scheduledChanges || []).some(c => c.kind === 'cancellation' && c.status === 'scheduled');
              const days = daysUntilExpiry(sub.expirationDate);
              const isExpiringSoon = days !== null && days <= 7 && sub.status === 'active';
              return (
                <Paper
                  key={sub._id}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: isExpiringSoon ? '#fde68a' : '#e0e0e0',
                    backgroundColor: isExpiringSoon ? '#fffbeb' : '#fff',
                    borderRadius: '12px',
                  }}
                >
                  {/* 상단: 이름 + 상태 */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body1" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sub.userId?.memberNickname || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {sub.userId?.memberEmail || '-'}
                      </Typography>
                    </Box>
                    <Box sx={statusChipSx(status.variant)}>{status.label}</Box>
                  </Box>

                  {/* 중간: 메타 정보 */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, fontSize: 12, color: 'text.secondary' }}>
                    <Box>📦 {productMap[sub.productId] || sub.productId}</Box>
                    <Box>🚗 {sub.vehicleId?.licensePlate || sub.vehicleLicensePlate || '-'}</Box>
                    <Box>📅 ~{formatDate(sub.expirationDate)}{isExpiringSoon && ' ⚠️'}</Box>
                    <Box>💧 {sub.washCount || 0}회</Box>
                  </Box>

                  {/* 하단: 액션 버튼 */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    {!isLegacySource && <Button
                      variant="contained"
                      color={isCancellationState ? 'warning' : 'primary'}
                      onClick={() => handleUnsubscribe(sub)}
                      sx={actionBtnSx}
                    >
                      {isCancellationState ? '해지취소' : '해지'}
                    </Button>}
                    {/* 해지는 결제 정보와 무관하므로 항상 제공한다. 환불만 조건부. */}
                    {canRefund(sub) ? (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleRefund(sub)}
                        sx={actionBtnSx}
                      >
                        환불
                      </Button>
                    ) : null}
                  </Box>
                </Paper>
              );
            })
          )}
        </Stack>
      ) : (
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        {/* 데스크톱: 전체 컬럼 테이블 */}
        <Table
          sx={{
            '& td[align="center"], & th[align="center"]': { whiteSpace: 'nowrap' },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              {/* 모바일 숨김 */}
              <TableCell width="4%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>No.</TableCell>
              <TableCell width="16%" sx={{ fontWeight: 700 }}>사용자</TableCell>
              <TableCell width="10%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>차량번호</TableCell>
              <TableCell width="8%" align="center" sx={{ fontWeight: 700 }}>상품</TableCell>
              <TableCell width="12%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>전화번호</TableCell>
              <TableCell width="7%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>세차횟수</TableCell>
              <TableCell
                width="11%"
                align="center"
                onClick={handleSortToggle}
                sx={{ fontWeight: 700, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}
              >
                만료일
                {sortOrder === 'asc'
                  ? <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: '#1976d2' }} />
                  : <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: '#1976d2' }} />
                }
              </TableCell>
              <TableCell width="8%" align="center" sx={{ fontWeight: 700 }}>상태</TableCell>
              <TableCell width="14%" align="center" sx={{ fontWeight: 700 }}>관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : paged.length > 0 ? (
              paged.map((sub, index) => {
                const status = statusMap[sub.status] || { label: sub.status, color: 'default' };
                const isCancellationState = sub.status === 'cancellation_requested' ||
                  (sub.scheduledChanges || []).some(c => c.kind === 'cancellation' && c.status === 'scheduled');
                const days = daysUntilExpiry(sub.expirationDate);
                const isExpiringSoon = days !== null && days <= 7 && sub.status === 'active';
                const isExpiring30 = days !== null && days <= 30 && days > 7 && sub.status === 'active';

                return (
                  <TableRow
                    key={sub._id}
                    hover
                    sx={{
                      backgroundColor: isExpiringSoon ? '#fff8e1' : 'inherit',
                    }}
                  >
                    <TableCell align="center" sx={{ color: '#888', display: { xs: 'none', md: 'table-cell' } }}>
                      {(page - 1) * limit + index + 1}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {sub.userId?.memberNickname || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                        {sub.userId?.memberEmail || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {sub.vehicleId?.licensePlate || sub.vehicleLicensePlate || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {productMap[sub.productId] || sub.productId}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#555', display: { xs: 'none', lg: 'table-cell' } }}>
                      {sub.userId?.memberPhone || '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {sub.washCount || 0}회
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 13, color: '#555' }}>
                          {formatDate(sub.expirationDate)}
                        </Typography>
                        {isExpiringSoon && (
                          <Tooltip title={`${days}일 후 만료`} arrow>
                            <WarningAmberIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
                          </Tooltip>
                        )}
                        {isExpiring30 && (
                          <Tooltip title={`${days}일 후 만료`} arrow>
                            <WarningAmberIcon sx={{ fontSize: 15, color: '#bdbdbd' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={statusChipSx(status.variant)}>
                        {status.label}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                        {!isLegacySource && <Button
                          variant="contained"
                          color={isCancellationState ? 'warning' : 'primary'}
                          onClick={() => handleUnsubscribe(sub)}
                          sx={actionBtnSx}
                        >
                          {isCancellationState ? '해지취소' : '해지'}
                        </Button>}
                        {canRefund(sub) ? (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleRefund(sub)}
                            sx={actionBtnSx}
                          >
                            환불
                          </Button>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#888' }}>
                  {expiryFilter || productFilter || search ? '필터 조건에 맞는 구독이 없습니다.' : '활성 구독이 없습니다.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="small"
            siblingCount={0}
            boundaryCount={1}
          />
        </Box>
      )}

      {/* 위험 액션 확인 모달 — 운영자 실수·분쟁 방지용 */}
      <ActionConfirmDialog
        open={actionDialog.open}
        action={actionDialog.action}
        sub={actionDialog.sub}
        reason={actionReason}
        onReasonChange={setActionReason}
        onClose={closeActionDialog}
        onConfirm={handleConfirmAction}
        loading={actionLoading}
      />

      {/* 이용권 수동 지급 — 결제 없이 기간을 지정해 멤버십 부여 */}
      <GrantMembershipDialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onGranted={() => {
          alert('이용권이 지급되었습니다.');
          fetchSubscriptions();
        }}
      />
    </Box>
  );
};

/**
 * 해지/취소/환불 액션 확인 다이얼로그
 * - 구독 상세(회원·차량·상품·만료·횟수) 명시 표시
 * - 환불은 사유 필수 입력 (분쟁 대응·감사 로그)
 * - 해지/취소는 사유 선택 입력
 */
const ActionConfirmDialog = ({ open, action, sub, reason, onReasonChange, onClose, onConfirm, loading }) => {
  if (!sub) return null;

  const titles = {
    unsubscribe: '구독 해지 예약',
    revert: '해지 예약 취소',
    refund: '구독 환불',
  };
  const confirmColors = {
    unsubscribe: 'warning',
    revert: 'primary',
    refund: 'error',
  };
  const description = {
    unsubscribe: '만료일까지는 정상 이용 가능. 만료 시 자동으로 해지됩니다.',
    revert: '예약된 해지를 취소하고 구독을 정상 상태로 되돌립니다.',
    refund: '⚠️ 즉시 환불 처리됩니다. 실제 결제 카드로 환불 금액이 반환되며 되돌릴 수 없습니다.',
  };
  const reasonRequired = action === 'refund';

  // 회원·구독 정보 — 모달 본문에 명시 표시
  const memberName = sub.userId?.memberNickname || sub.userId?.memberName || '-';
  const memberEmail = sub.userId?.memberEmail || '-';
  const memberPhone = sub.userId?.memberPhone || '-';
  const productName = productMap[sub.productId] || sub.productId || '-';
  const licensePlate = sub.vehicleId?.licensePlate || sub.licensePlate || '-';
  const expireDate = sub.expirationDate ? new Date(sub.expirationDate).toLocaleDateString('ko-KR') : '-';
  const nextPaymentDate = sub.nextPaymentDate ? new Date(sub.nextPaymentDate).toLocaleDateString('ko-KR') : '-';
  const washCount = sub.washCount ?? '-';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {titles[action] || '확인'}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity={action === 'refund' ? 'error' : action === 'unsubscribe' ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {description[action]}
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, rowGap: 1, columnGap: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">회원</Typography>
          <Typography variant="body2"><b>{memberName}</b> ({memberEmail})</Typography>

          <Typography variant="caption" color="text.secondary">연락처</Typography>
          <Typography variant="body2">{memberPhone}</Typography>

          <Typography variant="caption" color="text.secondary">상품</Typography>
          <Typography variant="body2">{productName}</Typography>

          <Typography variant="caption" color="text.secondary">차량번호</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{licensePlate}</Typography>

          <Typography variant="caption" color="text.secondary">이번 달 이용</Typography>
          <Typography variant="body2">{washCount}회</Typography>

          <Typography variant="caption" color="text.secondary">다음 결제일</Typography>
          <Typography variant="body2">{nextPaymentDate}</Typography>

          <Typography variant="caption" color="text.secondary">만료 예정일</Typography>
          <Typography variant="body2">{expireDate}</Typography>
        </Box>

        <TextField
          label={reasonRequired ? '환불 사유 (필수)' : '사유 (선택)'}
          required={reasonRequired}
          fullWidth
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder={
            action === 'refund'
              ? '예) 고객 요청, 결제 오류, 서비스 불만 등'
              : action === 'unsubscribe'
              ? '예) 고객 요청 해지'
              : '예) 해지 예약 철회 요청'
          }
          slotProps={{ htmlInput: { maxLength: 200 } }}
          helperText={`${reason.length}/200`}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>취소</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColors[action] || 'primary'}
          disabled={loading || (reasonRequired && !reason.trim())}
        >
          {loading ? '처리 중...' : (
            action === 'unsubscribe' ? '해지 예약' :
            action === 'revert' ? '해지 예약 취소' :
            action === 'refund' ? '환불 진행' : '확인'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionsPage;
